import { useMemo, useState } from "react";
import {
  TrendingDown,
  AlertTriangle,
  Activity,
  Home,
  Users,
  Wrench,
  Receipt,
  LineChart,
  Percent,
  ShieldAlert,
  Zap,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  computeSensitivityOverlays,
  SEVERITY_LABEL,
  type StressGroup,
  type StressOverlay,
  type StressSeverity,
} from "@/lib/sensitivity-overlay";
import type { ScoringInput } from "@/lib/scoring-engine";
import { verdictShort } from "@/lib/verdict-display";

interface SensitivityOverlayProps {
  scoringInput: ScoringInput;
  variant?: "memo" | "detail";
  eyebrow?: string;
  decisionVerdict?: string | null;
  dataConfidence?: number | null;
  noiTrend?: string | null;
}

const GROUP_ICON: Record<StressGroup, typeof Home> = {
  rent: Home,
  vacancy: Users,
  capex: Wrench,
  expenses: Receipt,
  exit_cap: LineChart,
  interest_rate: Percent,
};

const SEV_PILL: Record<StressSeverity, string> = {
  minor: "border-success/30 bg-success/10 text-success",
  moderate: "border-warning/30 bg-warning/10 text-warning",
  major: "border-severe/40 bg-severe/10 text-severe",
  breaks: "border-destructive/40 bg-destructive/10 text-destructive",
};

const SEV_BAR: Record<StressSeverity, string> = {
  minor: "bg-success/60",
  moderate: "bg-warning/70",
  major: "bg-severe/80",
  breaks: "bg-destructive",
};

const SEVERITY_RANK: Record<StressSeverity, number> = {
  breaks: 4,
  major: 3,
  moderate: 2,
  minor: 1,
};

function fmtDelta(v: number, suffix = ""): string {
  if (v === 0) return `0${suffix}`;
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(suffix === "%" ? 1 : 1)}${suffix}`;
}

function fmtDscrDelta(v: number | null): string {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}x`;
}

// Convert overlay into short condition phrase for the summary line.
function conditionPhrase(o: StressOverlay): string {
  switch (o.group) {
    case "rent":
      return o.label.toLowerCase().replace("market rent ", "rents fall ").replace("−", "");
    case "vacancy":
      return o.label.toLowerCase().replace("vacancy +", "vacancy rises ").replace(" pts", " pts");
    case "capex":
      return o.label.toLowerCase().replace("capex +", "CapEx increases ");
    case "expenses":
      return o.label.toLowerCase().replace("expenses +", "expenses rise ");
    case "exit_cap":
      return o.label.toLowerCase().replace("exit cap +", "exit cap expands ");
    case "interest_rate":
      return o.label.toLowerCase().replace("rate +", "rates rise ");
    default:
      return o.label.toLowerCase();
  }
}

export function SensitivityOverlay({ scoringInput, variant = "detail", eyebrow, decisionVerdict, dataConfidence, noiTrend }: SensitivityOverlayProps) {
  const [expanded, setExpanded] = useState(false);
  const result = useMemo(
    () => computeSensitivityOverlays(scoringInput, { decisionVerdict, dataConfidence, noiTrend }),
    [scoringInput, decisionVerdict, dataConfidence, noiTrend],
  );

  if (result.applicableCount === 0) {
    return (
      <div className="rounded-lg border border-border bg-muted/10 p-6 text-center">
        <AlertTriangle className="mx-auto h-6 w-6 text-muted-foreground" />
        <p className="mt-2 font-display text-sm font-semibold">Not enough inputs to stress-test</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Provide rent, vacancy, expenses, debt or exit cap to generate downside overlays.
        </p>
      </div>
    );
  }

  // Top-3 most impactful applicable overlays — ranked by severity then |scoreDelta|.
  const ranked = result.overlays
    .filter((o) => o.applicable)
    .slice()
    .sort((a, b) => {
      const r = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
      if (r !== 0) return r;
      return a.scoreDelta - b.scoreDelta; // more negative first
    });

  const topThree = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  // Build summary line from breaking / flipping scenarios. These are two
  // different claims: a break means the deal cannot survive the scenario, a
  // flip means it survives but stops clearing its cutoff. Merging them is how
  // a deal holding 1.26x coverage ended up labelled as failing.
  const breaking = ranked.filter((o) => o.severity === "breaks");
  const flipping = ranked.filter((o) => o.verdictFlipped && o.severity !== "breaks");
  let summaryLine = buildSummaryLine(breaking, flipping, ranked);
  if (result.fragility.isFragile && breaking.length === 0 && flipping.length === 0) {
    summaryLine = `Deal is fragile under baseline assumptions — ${result.fragility.reasons[0]}`;
  }

  return (
    <div className="space-y-5">
      {/* Header / eyebrow (memo mode) */}
      {variant === "memo" && eyebrow && (
        <div className="flex items-center gap-2">
          <span className="eyebrow text-muted-foreground">
            {eyebrow}
          </span>
          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="font-display text-base font-semibold tracking-tight">Sensitivity Overlay</h3>
        </div>
      )}

      {/* Where the deal breaks — hero callout + summary line */}
      {result.worst && (
        <WhereDealBreaks worst={result.worst} flipCount={result.flipCount} summary={summaryLine} fragile={result.fragility.isFragile} />
      )}

      {/* Top 3 impacts */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-muted-foreground">
            Top {topThree.length} impact{topThree.length === 1 ? "" : "s"}
          </p>
          {rest.length > 0 && (
            <span className="font-mono text-2xs text-muted-foreground">
              {rest.length} more hidden
            </span>
          )}
        </div>
        <div className="space-y-2">
          {topThree.map((o) => (
            <ImpactCard key={o.id} overlay={o} />
          ))}
        </div>
      </div>

      {/* Expand for the rest */}
      {rest.length > 0 && (
        <div className="space-y-2">
          {expanded && (
            <div className="space-y-2">
              {rest.map((o) => (
                <ImpactCard key={o.id} overlay={o} muted />
              ))}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="w-full gap-1.5 font-mono text-[11px] uppercase tracking-wider"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" /> Hide other scenarios
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" /> Show {rest.length} more scenario{rest.length === 1 ? "" : "s"}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function buildSummaryLine(
  breaking: StressOverlay[],
  flipping: StressOverlay[],
  ranked: StressOverlay[],
): string {
  if (breaking.length > 0) {
    const conds = breaking.slice(0, 2).map(conditionPhrase);
    const joined = conds.length === 2 ? `${conds[0]} or ${conds[1]}` : conds[0];
    return `This deal fails if ${joined}.`;
  }
  // Flips without a break — the deal still covers its debt, it just stops
  // clearing its cutoff. Report the downgrade, not a failure.
  const worstFlip = flipping[0];
  if (worstFlip) {
    const coverage = worstFlip.stressedDscr != null
      ? ` — coverage holds at ${worstFlip.stressedDscr.toFixed(2)}x`
      : "";
    return `Verdict falls to ${verdictShort(worstFlip.stressedVerdict)} if ${conditionPhrase(worstFlip)}${coverage}.`;
  }
  // Nothing broke or flipped — describe most fragile lever
  const worst = ranked[0];
  if (!worst) return "";
  return `Deal holds across stress — most fragile lever is ${conditionPhrase(worst)} (${Math.abs(worst.scoreDelta)} pt drop).`;
}

function ImpactCard({ overlay, muted = false }: { overlay: StressOverlay; muted?: boolean }) {
  const Icon = GROUP_ICON[overlay.group];
  const dscrAvail = overlay.baseDscr != null && overlay.stressedDscr != null;
  const noiAvail = overlay.noiDeltaPct != null;
  const irrAvail = overlay.baseIrr != null && overlay.stressedIrr != null;
  const barMag = Math.min(Math.abs(overlay.scoreDelta) / 25, 1);
  // A break and a verdict flip are different claims — see buildSummaryLine.
  const breaksDeal = overlay.severity === "breaks";
  const flipsVerdict = overlay.verdictFlipped && !breaksDeal;

  return (
    <div
      className={`rounded-lg border p-3 ${
        breaksDeal
          ? "border-destructive/40 bg-destructive/[0.04]"
          : flipsVerdict
          ? "border-severe/40 bg-severe/[0.04]"
          : muted
          ? "border-border/40 bg-card/20"
          : "border-border/60 bg-card/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-[13px] font-semibold text-foreground leading-tight">{overlay.label}</p>
              {breaksDeal && (
                <Badge
                  variant="outline"
                  className="border-destructive/40 bg-destructive/10 micro-label-xs text-destructive"
                >
                  Deal breaks under this condition
                </Badge>
              )}
              {flipsVerdict && (
                <Badge
                  variant="outline"
                  className="border-severe/40 bg-severe/10 micro-label-xs text-severe"
                >
                  Verdict flips to {verdictShort(overlay.stressedVerdict)}
                </Badge>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{overlay.description}</p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={`shrink-0 micro-label-xs ${SEV_PILL[overlay.severity]}`}
        >
          {SEVERITY_LABEL[overlay.severity]}
        </Badge>
      </div>

      {/* Impact bar */}
      <div className="mt-2.5 flex items-center gap-2">
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted/40">
          <div
            className={`h-full ${SEV_BAR[overlay.severity]} transition-all`}
            style={{ width: `${Math.max(barMag * 100, 4)}%` }}
          />
        </div>
        <span
          className={`font-mono text-[11px] font-semibold ${
            overlay.scoreDelta < 0 ? "text-destructive" : "text-success"
          }`}
        >
          {fmtDelta(overlay.scoreDelta)} pts
        </span>
      </div>

      {/* Outcome chips */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted-foreground">
        <span className="font-mono">
          Score <span className="text-foreground">{overlay.baseScore}→{overlay.stressedScore}</span>
        </span>
        {dscrAvail && (
          <span className="font-mono">
            DSCR{" "}
            <span className={`text-foreground ${overlay.stressedDscr! < 1.0 ? "text-destructive" : ""}`}>
              {overlay.baseDscr!.toFixed(2)}→{overlay.stressedDscr!.toFixed(2)}x
            </span>{" "}
            ({fmtDscrDelta(overlay.dscrDelta)})
          </span>
        )}
        {noiAvail && (
          <span className="font-mono">
            NOI{" "}
            <span className={`text-foreground ${overlay.noiDeltaPct! < 0 ? "text-destructive" : ""}`}>
              {fmtDelta(overlay.noiDeltaPct!, "%")}
            </span>
          </span>
        )}
        {irrAvail && (
          <span className="font-mono">
            IRR{" "}
            <span className="text-foreground">
              {overlay.baseIrr!.toFixed(1)}→{overlay.stressedIrr!.toFixed(1)}%
            </span>
          </span>
        )}
        <span className="ml-auto font-mono">
          Verdict{" "}
          <span className={`font-semibold ${overlay.verdictFlipped ? "text-destructive" : "text-foreground"}`}>
            {verdictShort(overlay.stressedVerdict)}
          </span>
          {overlay.verdictFlipped && <span className="ml-1 text-destructive">⚠</span>}
        </span>
      </div>
    </div>
  );
}

function WhereDealBreaks({
  worst,
  flipCount,
  summary,
  fragile = false,
}: {
  worst: StressOverlay;
  flipCount: number;
  summary: string;
  fragile?: boolean;
}) {
  const isBreak = worst.severity === "breaks";
  const isMajor = worst.severity === "major";
  const tone = isBreak || fragile
    ? { bg: "bg-destructive/5", border: "border-destructive/40", text: "text-destructive", Icon: ShieldAlert }
    : isMajor
    ? { bg: "bg-severe/5", border: "border-severe/40", text: "text-severe", Icon: TrendingDown }
    : { bg: "bg-warning/5", border: "border-warning/30", text: "text-warning", Icon: Zap };

  const headline = isBreak
    ? "Deal breaks under this condition."
    : fragile
    ? "Deal is fragile under baseline assumptions."
    : isMajor
    ? "This is the deal's most fragile lever."
    : "Deal holds up across realistic stress.";

  return (
    <div className={`rounded-lg border ${tone.border} ${tone.bg} p-4`}>
      <div className="flex items-start gap-3">
        <tone.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.text}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="eyebrow text-muted-foreground">
              Where the deal breaks
            </p>
            {flipCount > 0 && (
              <Badge
                variant="outline"
                className="border-destructive/30 bg-destructive/10 font-mono text-2xs uppercase text-destructive"
              >
                {flipCount} verdict flip{flipCount === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
          <p className={`mt-1 font-display text-base font-semibold tracking-tight ${tone.text}`}>{headline}</p>
          {summary && (
            <p className="mt-1.5 text-sm leading-relaxed text-foreground">{summary}</p>
          )}
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Worst lever: <span className="text-foreground">{worst.label}</span> · {worst.description}.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SensitivityOverlay;
